import {
  CART_STORAGE_KEY,
  addCartLine,
  parseCartLines,
  removeCartLine,
  serializeCartLines,
  type CartLine,
} from '@redevicer/shared'
import { useCallback, useEffect, useState } from 'react'

const CART_EVENT = 'redevicer:cart-updated'

function readLines(): CartLine[] {
  return parseCartLines(localStorage.getItem(CART_STORAGE_KEY))
}

function persist(lines: CartLine[]) {
  localStorage.setItem(CART_STORAGE_KEY, serializeCartLines(lines))
  window.dispatchEvent(new Event(CART_EVENT))
}

export function useCart() {
  const [lines, setLines] = useState<CartLine[]>(() => readLines())

  useEffect(() => {
    function sync() {
      setLines(readLines())
    }
    window.addEventListener(CART_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(CART_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const add = useCallback((line: CartLine): 'ok' | 'duplicate' | 'invalid' => {
    const prev = readLines()
    const next = addCartLine(prev, line)
    if (next === 'invalid') return 'invalid'
    if (next.length === prev.length) return 'duplicate'
    persist(next)
    setLines(next)
    return 'ok'
  }, [])

  const remove = useCallback((listingId: number) => {
    setLines((prev) => {
      const next = removeCartLine(prev, listingId)
      persist(next)
      return next
    })
  }, [])

  const clear = useCallback(() => {
    persist([])
    setLines([])
  }, [])

  return {
    lines,
    count: lines.length,
    add,
    remove,
    clear,
    listingIds: lines.map((l) => l.listing_id),
  }
}
